// src/components/job-engine/DeadLettersListPage.tsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Stack, Text, Dropdown, IDropdownOption, PrimaryButton, DefaultButton,
  MessageBar, MessageBarType, DetailsList, SelectionMode, IColumn, Selection,
  Dialog, DialogType, DialogFooter, Label,
} from "@fluentui/react";
import { isAxiosError } from "axios";
import { PaginationFooter } from "@/ui-controls/PaginationFooter";
import { requeueDeadLettersBulk } from "@/api/jobEngine";
import { useDeadLetters } from "./useDeadLetters";
import { useJobTypesMeta } from "./useJobTypesMeta";
import useJobEngineEvent from "./useJobEngineEvent";
import { useJobEngineConnectionState } from "./useJobEngineConnectionState";
import ConnectionStatusBadge from "./ConnectionStatusBadge";
import { formatDateTime } from "./formatters";
import type { DeadLetter, DeadLettersQuery } from "@/api/jobEngine";

const DeadLettersListPage: React.FC = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(25);
  const [jobType, setJobType] = useState<string>("");
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const meta = useJobTypesMeta();
  const query: DeadLettersQuery = { jobType: jobType || null, pageNumber, pageSize };
  const { items, totalCount, isLoading, error, refetch } = useDeadLetters(query);

  useJobEngineEvent("JobDeadLettered", refetch);

  const isConnected = useJobEngineConnectionState();
  React.useEffect(() => {
    if (isConnected) return;
    const id = window.setInterval(refetch, 10_000);
    return () => window.clearInterval(id);
  }, [isConnected, refetch]);

  const selection = useMemo(() => new Selection({
    onSelectionChanged: () => {
      const sel = selection.getSelection() as DeadLetter[];
      setSelectedIds(sel.map((x) => x.deadLetterId));
    },
    getKey: (item) => (item as DeadLetter).deadLetterId,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const doRequeue = async () => {
    setStatus(null);
    try {
      await requeueDeadLettersBulk(selectedIds);
      setStatus({ type: "success", text: `Requeued ${selectedIds.length}` });
      refetch();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 403) setStatus({ type: "error", text: "You don't have permission to requeue dead letters." });
      else if (isAxiosError(err)) setStatus({ type: "error", text: err.response?.data?.title ?? "Requeue failed" });
      else setStatus({ type: "error", text: "An unexpected error occurred." });
    } finally {
      setConfirmOpen(false);
    }
  };

  const jobTypeOptions: IDropdownOption[] = [
    { key: "", text: "Any" },
    ...(meta.data?.jobTypes ?? []).map((t) => ({ key: t.jobType, text: t.jobType })),
  ];

  const columns: IColumn[] = [
    { key: "jobType", name: "Type", fieldName: "jobType", minWidth: 140 },
    { key: "errorSummary", name: "Error", fieldName: "errorSummary", minWidth: 240 },
    { key: "deadLetteredAt", name: "Dead-lettered at", minWidth: 180, onRender: (d: DeadLetter) => formatDateTime(d.deadLetteredAt) },
    { key: "correlationId", name: "Correlation", fieldName: "correlationId", minWidth: 160 },
    { key: "link", name: "", minWidth: 60, onRender: (d: DeadLetter) => (
      <Link to={`/job-engine/dead-letters/${encodeURIComponent(d.deadLetterId)}`}>Open</Link>
    ) },
  ];

  return (
    <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
    <Stack tokens={{ childrenGap: 16 }}>
      <div className="page-header">
        <h2 className="page-title">Dead letters</h2>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Label>Type:</Label>
            <Dropdown
              options={jobTypeOptions}
              selectedKey={jobType}
              onChange={(_, o) => setJobType(String(o?.key ?? ""))}
              styles={{ root: { minWidth: 200 } }}
            />
          </Stack>
          <PrimaryButton text={`Requeue selected (${selectedIds.length})`} disabled={selectedIds.length === 0} onClick={() => setConfirmOpen(true)} />
          <ConnectionStatusBadge isConnected={isConnected} />
        </Stack>
      </div>

      {status && (
        <MessageBar messageBarType={status.type === "success" ? MessageBarType.success : MessageBarType.error}>
          {status.text}
        </MessageBar>
      )}
      {!isConnected && (
        <MessageBar messageBarType={MessageBarType.warning}>
          Live updates unavailable — polling.
        </MessageBar>
      )}
      {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

      <DetailsList
        items={items}
        columns={columns}
        selection={selection}
        selectionMode={SelectionMode.multiple}
        compact={true}
      />

      <PaginationFooter
        currentPage={pageNumber}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={(p) => setPageNumber(p)}
      />

      <Dialog
        hidden={!confirmOpen}
        onDismiss={() => setConfirmOpen(false)}
        dialogContentProps={{ type: DialogType.normal, title: "Requeue selected?", subText: `This will requeue ${selectedIds.length} dead letter(s).` }}
      >
        <DialogFooter>
          <PrimaryButton text="Requeue" onClick={doRequeue} />
          <DefaultButton text="Cancel" onClick={() => setConfirmOpen(false)} />
        </DialogFooter>
      </Dialog>
    </Stack>
    </div>
  );
};

export default DeadLettersListPage;
